import React, { useCallback } from 'react';
import { ButtonProps } from '@material-ui/core';
import { ConfirmedButton, LoadingIndicator } from '../components';
import { useForm } from 'react-hook-form';
import { useFormEvents } from '../forms';
import {
  Reservation,
  ReservationActivity,
  ReservationForms,
  reservationQuery,
  reservationService,
  ReservationStatus,
} from '@lib/state';
import { RefundableConfirmationForm, useObservable } from '../';
import { ReservationFeeList } from './reservation-fee-list';
import { useGetReservationChangeOptions } from '../utils';

interface Props extends Omit<ButtonProps, 'action' | 'onClick'> {
  reservation: Reservation;
  overrideable?: boolean;
  onSuccess?: () => void;
}

export const CheckOutReservationButton: React.FC<Props> = ({
  reservation,
  overrideable = false,
  onSuccess,
  ...props
}) => {
  const [{ status, error }, resetCheckOutReservationForm] = useFormEvents(
    ReservationForms.CheckOutReservation
  );

  const form = useForm();
  const activity = useObservable(reservationQuery.lastActivity);
  const overrideCode = form.watch('overrideCode');
  const {
    refetch: fetchReservationChangeOptions,
    isFetching: isLoading,
  } = useGetReservationChangeOptions();

  const resultView = <ReservationFeeList {...activity?.cost} />;

  const allowed = reservation.status === ReservationStatus.CheckedIn;

  let confirmView = (
    <RefundableConfirmationForm
      reservation={reservation}
      actionType={ReservationActivity.CheckOut}
      overrideable={overrideable}
      form={form}
    />
  );

  if (isLoading) {
    confirmView = <LoadingIndicator />;
  }

  const onOpen = useCallback(() => {
    resetCheckOutReservationForm();
    fetchReservationChangeOptions(reservation, ReservationActivity.CheckOut);
  }, [fetchReservationChangeOptions, resetCheckOutReservationForm, reservation]);
  return (
    <ConfirmedButton
      name="checkOutReservation"
      action={`Check Out Reservation ${reservation.recordNumber}`}
      onConfirm={() => reservationService.checkOutReservation(reservation, overrideCode)}
      onOpen={onOpen}
      buttonText="Check Out"
      status={status}
      error={error}
      hidden={!allowed}
      confirmView={confirmView}
      resultView={resultView}
      onSuccess={onSuccess}
      actionsHidden={isLoading}
      {...props}
    />
  );
};
